package net.frozenbit.games.soundgame.viewmodels

import androidx.lifecycle.ViewModel
import androidx.lifecycle.viewModelScope
import io.ktor.client.plugins.websocket.*
import kotlinx.coroutines.*
import kotlinx.coroutines.flow.*
import net.frozenbit.games.soundgame.KeyValueStore
import net.frozenbit.games.soundgame.audio.AudioManager
import net.frozenbit.games.soundgame.audio.MusicManager
import net.frozenbit.games.soundgame.dtos.*
import net.frozenbit.games.soundgame.utils.Countdown
import net.frozenbit.games.soundgame.utils.TimeCorrection
import net.frozenbit.games.soundgame.utils.createClient
import net.frozenbit.games.soundgame.utils.permanentSocketSession
import net.frozenbit.games.soundgame.viewmodels.HostViewModel.HostUiState.*
import net.frozenbit.games.soundgame.viewmodels.HostViewModel.HostUiState.SoundSelection.Participant
import net.frozenbit.games.soundgame.viewmodels.HostViewModel.HostUiState.WinnerPresentation.Entries
import net.frozenbit.games.soundgame.viewmodels.deeplink.joinLink
import net.frozenbit.games.soundgame.viewmodels.presentation.PlayerTagData
import net.frozenbit.games.soundgame.viewmodels.presentation.SoundCreation
import kotlin.time.Duration.Companion.minutes
import kotlin.time.Duration.Companion.seconds

private val SCORE_PRESENTATION_DURATION = 5.seconds

class HostViewModel internal constructor(
    private val keyValueStore: KeyValueStore,
    private val sessionKeyValueStore: KeyValueStore,
    private val audioManager: AudioManager,
    private val musicManager: MusicManager,
) : ViewModel() {
    private val _uiState = MutableStateFlow<HostUiState>(HostUiState.Idle(loading = false))
    val uiState: StateFlow<HostUiState> = _uiState

    private var currentSessionId: String? = null

    private val timeCorrection = TimeCorrection()

    private val nextPhaseCountdown = Countdown()

    val countdownTicks = nextPhaseCountdown.tickFlow()

    private val _outgoingMessages = MutableSharedFlow<HostClientMessage>(
        replay = 0,
        extraBufferCapacity = 10,
    )

    private val _musicMasterVolume = MutableStateFlow(1.0f)
    val musicMasterVolume: StateFlow<Float> = _musicMasterVolume

    init {
        setMusicMasterVolume(0.8f)
    }

    private val audioEventGenerator = AudioEventGenerator(timeCorrection)
        .also { generator ->
            viewModelScope.launch {
                generator.play(audioManager, musicManager)
            }
            viewModelScope.launch {
                generator.votingPlaybackState
                    .map { it == AudioEventGenerator.VotingPlaybackState.Finished }
                    .distinctUntilChanged()
                    .collect { audioFinished ->
                        if (audioFinished) {
                            _outgoingMessages.tryEmit(VotingOptionsPresentationFinished())
                        }
                    }
            }
            viewModelScope.launch {
                generator.votingPlaybackState
                    .map { it == AudioEventGenerator.VotingPlaybackState.WinnerFinished }
                    .distinctUntilChanged()
                    .collect { audioFinished ->
                        if (audioFinished) {
                            delay(SCORE_PRESENTATION_DURATION)
                            _outgoingMessages.tryEmit(WinnerPresentationFinished())
                        }
                    }
            }
            viewModelScope.launch {
                generator.votingPlaybackState
                    .collect { playbackState ->
                        _uiState.update { currentState ->
                            when (currentState) {
                                is Voting ->
                                    currentState.spoilerFreePresentation(playbackState)

                                is WinnerPresentation ->
                                    currentState.awardPresentation(playbackState)

                                else ->
                                    currentState
                            }
                        }
                    }
            }
        }

    private var gameLoop: Job = viewModelScope.launch {
        _uiState.value = Idle(loading = true)
        val client = createClient()
        client.permanentSocketSession(
            url = "host",
            onReconnected = {
                currentSessionId?.let { currentSessionId ->
                    sendToServer(
                        CreateGame(
                            sessionId = currentSessionId,
                        )
                    )
                }
            }
        ) {
            val sendJob = launch { _outgoingMessages.collect { sendToServer(it) } }
            while (currentCoroutineContext().isActive) {
                val serverMessage = try {
                    receiveDeserialized<HostServerMessage>()
                } catch (e: Exception) {
                    println("Failed to deserialize message from host: $e")
                    break
                }
                when (serverMessage) {
                    HostIdleUpdate -> {
                        _uiState.value = Idle(loading = false)
                    }
                    is HostLobbyUpdate -> {
                        updateData(serverMessage.common)
                        _uiState.value = Lobby(
                            joinCode = serverMessage.common.joinCode,
                            joinLink = joinLink(serverMessage.common.joinCode),
                            players = serverMessage.players.map {
                                it.playerTag()
                            },
                        )
                    }

                    is GameCreationFailed -> {
                        _uiState.value = Idle(loading = false)
                    }

                    is HostPromptSelectionUpdate -> {
                        updateData(serverMessage.common)
                        val actualStartTime = timeCorrection.actual(serverMessage.startedAt)
                        val actualEndTime = timeCorrection.actual(serverMessage.expiresAt)
                        nextPhaseCountdown.start(start = actualStartTime, target = actualEndTime)
                        _uiState.value = PromptSelection(
                            joinCode = serverMessage.common.joinCode,
                            selectingPlayer = serverMessage.selectingPlayer.playerTag(),
                        )
                    }

                    is HostSoundSelectionUpdate -> {
                        updateData(serverMessage.common)
                        val actualStartTime = timeCorrection.actual(serverMessage.startedAt)
                        val actualEndTime = timeCorrection.actual(serverMessage.expiresAt)
                        nextPhaseCountdown.start(start = actualStartTime, target = actualEndTime)
                        _uiState.value = SoundSelection(
                            joinCode = serverMessage.common.joinCode,
                            jury = serverMessage.jury.playerTag(),
                            participants = serverMessage.participants.map {
                                Participant(
                                    player = it.player.playerTag(),
                                    picked = it.picked,
                                )
                            },
                            promptText = serverMessage.prompt.text,
                        )
                    }

                    is HostVotingUpdate -> {
                        updateData(serverMessage.common)
                        val actualStartTime = serverMessage.startedAt?.let { timeCorrection.actual(it) }
                        val actualEndTime = serverMessage.expiresAt?.let { timeCorrection.actual(it) }
                        if (actualStartTime != null && actualEndTime != null) {
                            nextPhaseCountdown.start(start = actualStartTime, target = actualEndTime)
                        } else {
                            nextPhaseCountdown.clear()
                        }
                        val playbackState = audioEventGenerator
                            .votingPlaybackState
                            .value
                        _uiState.value = Voting(
                            joinCode = serverMessage.common.joinCode,
                            jury = serverMessage.jury.playerTag(),
                            voteOptions = serverMessage.options.map { creation ->
                                SoundCreation(
                                    highlighted = false,
                                    play1Indicator = false,
                                    line1 = creation.sound1Title,
                                    line1Visible = false,
                                    play2Indicator = false,
                                    line2 = creation.sound2Title,
                                    line2Visible = false,
                                )
                            },
                            promptText = serverMessage.prompt.text,
                        ).spoilerFreePresentation(playbackState)
                    }

                    is HostReplayVoteOption -> {
                        viewModelScope.launch {
                            audioEventGenerator.replayEntry(audioManager, serverMessage.optionIndex)
                        }
                    }

                    is HostWinnerUpdate -> {
                        updateData(serverMessage.common)
                        _uiState.value = WinnerPresentation(
                            joinCode = serverMessage.common.joinCode,
                            jury = serverMessage.jury.playerTag(),
                            promptText = serverMessage.prompt.text,
                            entries = serverMessage.entries.map { entry ->
                                Entries(
                                    author = entry.author.playerTag(),
                                    creation = SoundCreation(
                                        highlighted = entry.winner,
                                        play1Indicator = false,
                                        line1 = entry.sound1Title,
                                        line1Visible = true,
                                        play2Indicator = false,
                                        line2 = entry.sound2Title,
                                        line2Visible = true,
                                    ),
                                    points = entry.points,
                                    revealPoints = false,
                                )
                            },
                        )
                    }

                    is HostGameOverUpdate -> {
                        updateData(serverMessage.common)
                        _uiState.value = GameOver(
                            joinCode = serverMessage.common.joinCode,
                            scorings = serverMessage.scores.mapIndexed { index, scoring ->
                                GameOver.Scoring(
                                    player = scoring.player.playerTag(),
                                    points = scoring.score,
                                    winner = index == serverMessage.winnerIndex,
                                )
                            },
                            credits = serverMessage.creditText,
                        )
                    }
                }
                audioEventGenerator.feed(serverMessage)
            }
            sendJob.cancel()
        }
        client.close()
    }

    private fun updateData(common: HostCommonGameData) {
        currentSessionId = common.sessionId
        sessionKeyValueStore.set(
            "host.sessionId",
            common.sessionId,
            maxLifetime = 3.minutes,
        )
        timeCorrection.calibrate(common.serverTime)
    }

    private fun Voting.spoilerFreePresentation(
        playbackState: AudioEventGenerator.VotingPlaybackState,
    ): Voting {
        val playbackStateInfo = playbackState.present()
        return copy(
            voteOptions = voteOptions.mapIndexed { index, existing ->
                existing.copy(
                    highlighted = index == playbackStateInfo.currentlyPlayingOption,
                    play1Indicator =
                        index == playbackStateInfo.currentlyPlayingOption
                                && playbackStateInfo.currentlyPlayingSound == 1,
                    line1Visible = (index == playbackStateInfo.revealUpToOption
                            && playbackStateInfo.revealedSounds >= 1)
                            || index < playbackStateInfo.revealUpToOption,
                    play2Indicator =
                        index == playbackStateInfo.currentlyPlayingOption
                                && playbackStateInfo.currentlyPlayingSound == 2,
                    line2Visible = (index == playbackStateInfo.revealUpToOption
                            && playbackStateInfo.revealedSounds >= 2)
                            || index < playbackStateInfo.revealUpToOption,
                )
            },
        )
    }

    private fun WinnerPresentation.awardPresentation(
        playbackState: AudioEventGenerator.VotingPlaybackState,
    ): WinnerPresentation {
        val playbackStateInfo = playbackState.present()
        return copy(
            entries = entries.mapIndexed { index, existing ->
                existing.copy(
                    creation = existing.creation.copy(
                        play1Indicator = index == playbackStateInfo.currentlyPlayingOption
                                && playbackStateInfo.currentlyPlayingSound == 1,
                        play2Indicator = index == playbackStateInfo.currentlyPlayingOption
                                && playbackStateInfo.currentlyPlayingSound == 2,
                        line1Visible = (index != playbackStateInfo.currentlyPlayingOption)
                                || (playbackStateInfo.revealedSounds >= 1),
                        line2Visible = (index != playbackStateInfo.currentlyPlayingOption)
                                || (playbackStateInfo.revealedSounds >= 2),
                    ),
                    revealPoints = playbackState == AudioEventGenerator.VotingPlaybackState.WinnerFinished,
                )
            },
        )
    }

    private suspend fun DefaultClientWebSocketSession.sendToServer(message: HostClientMessage) {
        sendSerialized(message)
    }

    fun startHosting() {
        _uiState.value = Idle(loading = true)
        _outgoingMessages.tryEmit(
            CreateGame(
                sessionId = sessionKeyValueStore.get("host.sessionId"),
            )
        )
    }

    fun setMusicMasterVolume(volume: Float) {
        _musicMasterVolume.value = volume
        musicManager.setMasterVolume(volume.toDouble())
    }

    sealed class HostUiState {
        data class Idle(
            val loading: Boolean,
        ) : HostUiState()

        data class Lobby(
            val joinCode: String,
            val joinLink: String,
            val players: List<PlayerTagData>,
        ) : HostUiState()

        data class PromptSelection(
            val joinCode: String,
            val selectingPlayer: PlayerTagData,
        ) : HostUiState()

        data class SoundSelection(
            val joinCode: String,
            val jury: PlayerTagData,
            val participants: List<Participant>,
            val promptText: String,
        ) : HostUiState() {
            data class Participant(
                val player: PlayerTagData,
                val picked: Boolean,
            )
        }

        data class Voting(
            val joinCode: String,
            val jury: PlayerTagData,
            val promptText: String,
            val voteOptions: List<SoundCreation>,
        ) : HostUiState()

        data class WinnerPresentation(
            val joinCode: String,
            val jury: PlayerTagData,
            val promptText: String,
            val entries: List<Entries>,
        ) : HostUiState() {
            data class Entries(
                val author: PlayerTagData,
                val creation: SoundCreation,
                val points: Int,
                val revealPoints: Boolean,
            )
        }

        data class GameOver(
            val joinCode: String,
            val scorings: List<Scoring>,
            val credits: String,
        ) : HostUiState() {
            data class Scoring(
                val player: PlayerTagData,
                val points: Int,
                val winner: Boolean,
            )
        }
    }
}

private fun WsPlayer.playerTag() = PlayerTagData(
    name,
    avatarIndex,
    offline,
)
